/* eslint-disable react/no-danger */
/* eslint-disable max-lines */
import { Config } from '@cian/config';
import { ErrorLogComponent } from '@cian/error-log-component';
import { IHttpApi } from '@cian/http-api/shared';
import { ILogger } from '@cian/logger';
import { Spinner } from '@cian/ui-kit';
import classNames from 'classnames';
import * as React from 'react';

import { readAllOffersForSavedSearches } from '../../../api/saved_searches/readAllOffers';
import { NewbuildingBlackFridayTopBanner } from '../../../components/NewbuildingBlackFridayTopBanner';
import { ChatPopupContainer } from '../../../containers/ChatPopupContainer';
import { FavoritesFoldersContainer } from '../../../containers/FavoritesFoldersContainer';
import { FiltersContainer } from '../../../containers/FiltersContainer';
import { HeaderSectionContainer } from '../../../containers/HeaderSectionContainer';
import { JournalHighlightsContainer } from '../../../containers/JournalHighlightsContainer';
import { NewbuildingBrokerBannerContainer } from '../../../containers/NewbuildingBrokerBannerContainer';
import { NewbuildingPromoBuilderOffersContainer } from '../../../containers/NewbuildingPromoBuilderOffersContainer';
import { LazyNewbuildingPromoCarouselContainer } from '../../../containers/NewbuildingPromoCarousel';
import { NotAvailablePopupContainer } from '../../../containers/NotAvailablePopupContainer';
import { PaginationContainer } from '../../../containers/PaginationContainer';
import { ProfessionalSearchPromoContainer } from '../../../containers/ProfessionalSearchPromoContainer';
import { RegionalDistrictsModalContainer } from '../../../containers/RegionalDistrictsModalContainer';
import { SeoMarkedListContainer } from '../../../containers/SeoMarkedListContainer';
import { SeoTextContainer } from '../../../containers/SeoTextContainer';
import { SpecialProjectBannerContainer } from '../../../containers/SpecialProjectBannerContainer';
import { UndergroundMapModalContainer } from '../../../containers/UndergroundMapContainer';
import { VillagesRecommendationsContainer } from '../../../containers/VillagesRecommendationsContainer';
import { DirectionsContainer } from '../../../filters/components/directions/modal/container';
import { DistrictsContainer } from '../../../filters/components/districts/container';
import LocationSwitherContainer from '../../../filters/components/location_switcher/container';
import { MapContainer } from '../../../filters/components/map/container';
import { TTag } from '../../../filters/state/tags';
import { IJsonQuery } from '../../../json_query';
import { IOffer } from '../../../offer/types';
import { SeoTopBanners } from '../../../serp/components/adfox/seo_top_banners';
import { AdFoxBannerType } from '../../../serp/components/adfox/templates';
import { RealtorRequestTrapContainer } from '../../../serp/components/demand';
import { FastLinksContainer } from '../../../serp/components/fast_links/container';
import { HideOfferTopPopup } from '../../../serp/components/hide_offer_top_popup';
import { NewobjectsContainer } from '../../../serp/components/newobjects/container';
import { NewobjectsFromDeveloperContainer } from '../../../serp/components/newobjects_from_developer/container';
import { OffersContainer } from '../../../serp/components/offers/container';
import { PromoteMobile } from '../../../serp/components/promote_mobile';
import { SuggestionsContainer } from '../../../serp/components/suggestions/container';
import { ComparisonNotificationContainerLoadable } from '../../../serp/containers/ComparisonNotificationContainer';
import { EmptyPageSummaryContainer } from '../../../serp/containers/EmptyPageSummaryContainer';
import { QuickLinksContainer } from '../../../serp/containers/QuickLinksContainer';
import { SeoBotCatcherLinksContainer } from '../../../serp/containers/SeoBotCatcherLinksContainer';
import { SeoCrossLinksContainer } from '../../../serp/containers/SeoCrossLinksContainer';
import { SuburbanCatalogsContainer } from '../../../serp/containers/SuburbanCatalogsContainer';
import { EHideOfferState } from '../../../serp/state/hide_offer';
import { IComparisonNotification } from '../../../serp/state/offersComparison';
import { ILocation, TLocation } from '../../../types/location';
import { IQuickLinks } from '../../../types/quickLinks';
import { TSuburbanCatalog } from '../../../types/suburbanCatalog';
import { TUser } from '../../../types/user';
import { isArrayWithItems } from '../../../utils';
import { parseHash } from '../../../utils/hash';
import { logComponentError } from '../../../utils/log_component_error';
import { IMakeRequest } from '../../../utils/request';
import { saveJsonQueryInLocalStorage } from '../../../utils/save_json_query';
import { shouldSendAdsEvent } from '../../../utils/shouldSendAdsEvent';
import { TDeviceType } from '../../state';
import { ChatsContainerLoadable } from '../ChatsContainer';
import { NewbuildingConsultantPopupContainer } from '../NewbuildingConsultantPopup';
import { PreInfiniteBanner } from '../PreInfiniteBanner';
import { EPreInfiniteBannerTypes } from '../PreInfiniteBanner/PreInfiniteBanner';

const style = require('./index.css');

export interface IContext {
  logger: ILogger;
}

export interface ISearchEngineResultsPageProps {
  currency: string;
  context: IContext;
  deviceType: TDeviceType;
  baseUrl: string;
  isFetching: boolean;
  isPromotionMobileClosed: boolean;
  isPrintEnabled: boolean;
  isSpecialProjectAdfoxBannerVisible: boolean;
  isNewbuildingPromoBuilderOffersVisible: boolean;
  isNewbuildingBrokerBannerVisible: boolean;
  isProfessionalSearchPromoBannerVisible: boolean;
  isBlackFridayBannerVisible: boolean;
  subdomain: string;
  offers: IOffer[];
  quickLinks: IQuickLinks | null;
  aggregatedOffers: number;

  regions: ILocation[];
  currentLocation: TLocation;
  jsonQuery: IJsonQuery;
  tags: TTag[];

  user: TUser;
  isDirectionsModalVisible: boolean;
  isDistrictModalVisible: boolean;
  isLocationSwitcherVisible: boolean;
  isMapModalVisible: boolean;
  isRegionalDistrictsModalVisible: boolean;
  isStalkerBannerShown: boolean;
  isMetroModalVisible: boolean;
  isOfficeCalculatorVisible: boolean;
  isSignInTriggerModalOpen: boolean;
  isFilterByFlatsEnabled: boolean;
  seoTgbType: AdFoxBannerType;
  showTrap: boolean;
  makeRequest: IMakeRequest;
  isTopPopupOpened: boolean;
  hideOfferState: EHideOfferState;
  hideOfferErrorMessage: string;
  lkUrl: string;

  httpApi: IHttpApi;
  queryString: string;

  showPreInfinteSaveSearchBanner?: boolean;

  currentUrl: string | undefined;
  isOfferInSavedSearch?: boolean;
  config: Config;

  comparisonNotification: IComparisonNotification | null;
  additionalPuids: { [key: string]: string };
  suburbanCatalogsTypes: TSuburbanCatalog[];
}

export interface ISearchEngineResultsPageDispatchProps {
  setHideOfferState(hideOfferState: EHideOfferState): void;
  setLoading(): void;
}

export interface ISearchEngineResultsState {
  isMounted: boolean;
}

export type TSearchEngineResultsPageProps = ISearchEngineResultsPageProps & ISearchEngineResultsPageDispatchProps;

export class SearchEngineResultsPage extends React.Component<TSearchEngineResultsPageProps, ISearchEngineResultsState> {
  public context: IContext;
  public state = {
    isMounted: false,
  };

  /* istanbul ignore next */
  public componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    logComponentError({
      error,
      errorInfo,
      logger: this.props.context.logger,
    });
  }

  /* istanbul ignore next */
  public componentDidMount() {
    const { setHideOfferState, httpApi, context, queryString, jsonQuery } = this.props;

    saveJsonQueryInLocalStorage(jsonQuery);

    /**
     * При уходе с выдачи по сохраненному поиску отметить все "новые" оферы просмотренными
     */
    window.addEventListener('beforeunload', () => {
      const searchParams = new URLSearchParams(queryString);
      if (searchParams.has('saved_search_id')) {
        const savedSearchId = Number(searchParams.get('saved_search_id'));
        readAllOffersForSavedSearches(httpApi, { savedSearchId }, context.logger);
      }
    });

    /* istanbul ignore next */
    window.addEventListener('popstate', async () => {
      this.props.setLoading();
      window.location.reload();
    });

    const parsedHash = parseHash(window.location.hash);
    if (Object.keys(parsedHash).includes('HIDE')) {
      setHideOfferState(parsedHash['HIDE'] as EHideOfferState);
      history.pushState('', document.title, window.location.pathname + window.location.search);
    }

    this.setState({ isMounted: true });
  }

  /* istanbul ignore next */
  public componentWillUnmount() {
    this.setState({ isMounted: false });
  }

  /* istanbul ignore next */
  public render() {
    const {
      baseUrl,
      deviceType,
      isFetching,
      isPrintEnabled,
      isPromotionMobileClosed,
      offers,
      quickLinks,
      makeRequest,
      isTopPopupOpened,
      hideOfferState,
      hideOfferErrorMessage,
      showTrap,
      isOfferInSavedSearch,
      isFilterByFlatsEnabled,
      comparisonNotification,
      additionalPuids,
      seoTgbType,
      isSpecialProjectAdfoxBannerVisible,
      isNewbuildingBrokerBannerVisible,
      isNewbuildingPromoBuilderOffersVisible,
      isProfessionalSearchPromoBannerVisible,
      isBlackFridayBannerVisible,
      suburbanCatalogsTypes,
    } = this.props;

    const isQuickLinksAvailable =
      quickLinks &&
      isArrayWithItems(quickLinks.links) &&
      quickLinks.links.some(({ offersCount }) => Boolean(offersCount));

    return (
      <div className={classNames(style['serp'], style['serp--light'])}>
        <SeoTextContainer />
        <SeoMarkedListContainer />
        <ErrorLogComponent logger={this.props.context.logger}>
          <FiltersContainer />
        </ErrorLogComponent>
        {isFetching && (
          <div className={style['preloadOverlay']}>
            <div className={style['preloader']}>
              <Spinner size={50} />
            </div>
          </div>
        )}
        <ErrorLogComponent logger={this.props.context.logger}>
          <HeaderSectionContainer />
        </ErrorLogComponent>

        <ErrorLogComponent logger={this.props.context.logger}>
          <EmptyPageSummaryContainer />
        </ErrorLogComponent>

        {deviceType !== 'desktop' && !isPromotionMobileClosed && (
          <ErrorLogComponent logger={this.props.context.logger}>
            <PromoteMobile />
          </ErrorLogComponent>
        )}
        {isQuickLinksAvailable && (
          <ErrorLogComponent logger={this.props.context.logger}>
            <QuickLinksContainer
              banner={
                suburbanCatalogsTypes.length <= 1 ? (
                  <SuburbanCatalogsContainer catalogsTypes={suburbanCatalogsTypes} />
                ) : undefined
              }
            />
          </ErrorLogComponent>
        )}
        {(!isQuickLinksAvailable || suburbanCatalogsTypes.length > 1) && (
          <ErrorLogComponent logger={this.props.context.logger}>
            <SuburbanCatalogsContainer catalogsTypes={suburbanCatalogsTypes} fullWidth />
          </ErrorLogComponent>
        )}
        <ErrorLogComponent logger={this.props.context.logger}>
          {isNewbuildingPromoBuilderOffersVisible ? (
            <NewbuildingPromoBuilderOffersContainer />
          ) : isSpecialProjectAdfoxBannerVisible ? (
            <SpecialProjectBannerContainer />
          ) : isNewbuildingBrokerBannerVisible ? (
            <NewbuildingBrokerBannerContainer />
          ) : isProfessionalSearchPromoBannerVisible ? (
            <ProfessionalSearchPromoContainer />
          ) : isBlackFridayBannerVisible ? (
            <NewbuildingBlackFridayTopBanner />
          ) : null}
        </ErrorLogComponent>
        <ErrorLogComponent logger={this.props.context.logger}>
          <OffersContainer />
        </ErrorLogComponent>
        <ErrorLogComponent logger={this.props.context.logger}>
          <NewobjectsContainer />
        </ErrorLogComponent>
        <ErrorLogComponent logger={this.props.context.logger}>
          <NewobjectsFromDeveloperContainer />
        </ErrorLogComponent>

        <ErrorLogComponent logger={this.props.context.logger}>
          {this.props.showPreInfinteSaveSearchBanner && (
            <PreInfiniteBanner
              type={EPreInfiniteBannerTypes.subscription}
              isOfferInSavedSearch={isOfferInSavedSearch}
            />
          )}
        </ErrorLogComponent>

        <ErrorLogComponent logger={this.props.context.logger}>
          <SuggestionsContainer />
        </ErrorLogComponent>

        <ErrorLogComponent logger={this.props.context.logger}>
          <PaginationContainer />
        </ErrorLogComponent>

        <ErrorLogComponent logger={this.props.context.logger}>
          <LazyNewbuildingPromoCarouselContainer />
        </ErrorLogComponent>

        <ErrorLogComponent logger={this.props.context.logger}>
          <VillagesRecommendationsContainer />
        </ErrorLogComponent>

        {showTrap && (
          <div className={style['wrapper']} data-testid="realtor_request_trap">
            <ErrorLogComponent logger={this.props.context.logger}>
              <RealtorRequestTrapContainer />
            </ErrorLogComponent>
          </div>
        )}
        <ErrorLogComponent logger={this.props.context.logger}>
          <FastLinksContainer />
        </ErrorLogComponent>
        {!isPrintEnabled && (
          <ErrorLogComponent logger={this.props.context.logger}>
            <SeoTopBanners
              offers={offers}
              baseUrl={baseUrl}
              isAgent={this.props.user.isAuthenticated && this.props.user.isAgent}
              makeRequest={makeRequest}
              additionalPuids={additionalPuids}
              type={seoTgbType}
              shouldSendAdsEvent={shouldSendAdsEvent(this.props.jsonQuery) || !isFilterByFlatsEnabled}
            />
          </ErrorLogComponent>
        )}

        <ErrorLogComponent logger={this.props.context.logger}>
          <JournalHighlightsContainer />
        </ErrorLogComponent>

        <ErrorLogComponent logger={this.props.context.logger}>
          <SeoCrossLinksContainer />
        </ErrorLogComponent>

        <ErrorLogComponent logger={this.props.context.logger}>
          <UndergroundMapModalContainer />
        </ErrorLogComponent>

        {this.props.isMapModalVisible && (
          <ErrorLogComponent logger={this.props.context.logger}>
            <MapContainer />
          </ErrorLogComponent>
        )}
        {this.props.isLocationSwitcherVisible && (
          <ErrorLogComponent logger={this.props.context.logger}>
            <LocationSwitherContainer />
          </ErrorLogComponent>
        )}
        {this.props.isDistrictModalVisible && (
          <ErrorLogComponent logger={this.props.context.logger}>
            <DistrictsContainer />
          </ErrorLogComponent>
        )}
        {this.props.isDirectionsModalVisible && (
          <ErrorLogComponent logger={this.props.context.logger}>
            <DirectionsContainer />
          </ErrorLogComponent>
        )}
        {this.props.isRegionalDistrictsModalVisible && (
          <ErrorLogComponent logger={this.props.context.logger}>
            <RegionalDistrictsModalContainer />
          </ErrorLogComponent>
        )}
        {this.state.isMounted && isTopPopupOpened && (
          <HideOfferTopPopup
            isError={hideOfferState === EHideOfferState.ERROR}
            lkUrl={this.props.lkUrl}
            errorMessage={hideOfferErrorMessage}
          />
        )}
        <NewbuildingConsultantPopupContainer />
        <ChatsContainerLoadable />
        {/** TODO: Возможно стоит подумать об архитектуре 3х разных чатов */}
        <ChatPopupContainer />

        <NotAvailablePopupContainer />

        <FavoritesFoldersContainer />

        <ErrorLogComponent logger={this.props.context.logger}>
          <SeoBotCatcherLinksContainer />
        </ErrorLogComponent>
        {comparisonNotification && <ComparisonNotificationContainerLoadable notification={comparisonNotification} />}
      </div>
    );
  }
}
